import React, { useState, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'apollo-3';
import qs from 'query-string';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Flex } from 'Components/Base';
import TabList, { SCROLL_STATE } from 'Components/TabList';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { PATHS } from 'Routes/Main/paths';
import { getGuidedPractices } from 'GraphQL/query/GuidedPractice.query';
import Loading from 'Components/Loading';
import EmptyState from 'Routes/GuidedPractice/Components/EmptyState';
import { useConfig } from 'Context/ConfigContext';
import { useCapacitor } from 'Util/Capacitors';
import { navigateToTungKai } from 'Util/navigateToTungKai';
import * as storage from 'Util/Capacitors/CapacitorStorage';

import Header from '../../Components/Header';
import InnerLayout from '../../Components/InnerLayout';
import GuidedPracticeCard from '../../Components/GuidedPracticeCard';
import GuidedPracticeTutorialModal from '../GuidedPracticeTutorialModal';

const GuidedPracticeListWrapper = styled(Flex)`
  flex: 1;
  flex-direction: column;
  padding: ${(props) => (props.isTablet ? '16px' : '24px')};
  background-color: ${({ theme }) => theme.colors.backgroundNeutralLightgrey};
  border-radius: 0px 0px 8px 8px;
  overflow-y: scroll;
  gap: 8px;

  /* Webkit browsers (Chrome, Safari) */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Firefox */
  scrollbar-width: none;
  scrollbar-color: transparent transparent;
`;

const GuidedPracticeListContent = ({
  items = [],
  onClickItem,
  isTablet = false,
  isMobile = false,
}) => {
  return (
    <GuidedPracticeListWrapper isTablet={isTablet}>
      {items.map((item) => (
        <GuidedPracticeCard
          key={item.id}
          item={item}
          onClick={() => onClickItem(item)}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      ))}
    </GuidedPracticeListWrapper>
  );
};
GuidedPracticeListContent.propTypes = {
  items: PropTypes.array,
  onClickItem: PropTypes.func,
  isTablet: PropTypes.bool,
  isMobile: PropTypes.bool,
};

const DEFAULT_SUBJECT = 'all';
const TUNGKAI_REDIRECT_KEY = 'guidedPracticeTungKaiRedirectPath';

const GuidedPracticeList = () => {
  const history = useHistory();
  const location = useLocation();
  const { subjectList: defaultSubjectList, tungKai, userApi } = useConfig();
  const { openInApp } = useCapacitor();
  const { isMobile, isTablet } = useCompatibleDisplay();
  const { t } = useTranslation('guidedPractice');
  const { data, loading } = useQuery(getGuidedPractices, {
    fetchPolicy: 'no-cache',
  });

  const [currentSubject, setCurrentSubject] = useState(DEFAULT_SUBJECT);
  const [scrollState, setScrollState] = useState(SCROLL_STATE.START);

  // Only TungKai deep-links pass `?redirectPath`; persist it so sub-routes (which drop the query) still return to TungKai.
  const { redirectPath: redirectPathFromQuery } = qs.parse(location.search);

  useEffect(() => {
    if (redirectPathFromQuery) storage.setItem(TUNGKAI_REDIRECT_KEY, `${redirectPathFromQuery}`);
  }, [redirectPathFromQuery]);

  const handleBack = async () => {
    const redirectPath =
      redirectPathFromQuery || (await storage.getItem(TUNGKAI_REDIRECT_KEY)) || '/home';
    await storage.removeItem(TUNGKAI_REDIRECT_KEY);
    return navigateToTungKai({
      redirectPath: `${redirectPath}`,
      config: { userApi, tungKai },
      openInApp,
    });
  };

  const subjects = useMemo(() => {
    const mapping = { [DEFAULT_SUBJECT]: 'ทั้งหมด' };
    defaultSubjectList.forEach((s) => (mapping[s.key] = s.description));
    return mapping;
  }, [defaultSubjectList]);

  // NOTE: For multiple subjects
  // const itemKeys = useMemo(() => subjectList.map((s) => s.key), []);

  const handleGoToGuidedPractice = (guidedPractice) => {
    const { guidedPracticeUserId, latestExamPaperId, latestExamSessionId } = guidedPractice;
    if (guidedPractice.status === 'END') {
      history.push(
        `${PATHS.guidedPractice}/${guidedPracticeUserId}/exam-room/${latestExamPaperId}/session/${latestExamSessionId}/results`
      );
    } else if (guidedPractice.status === 'NOT_STARTED' || guidedPractice.status === 'IN_PROGRESS') {
      history.push(`${PATHS.guidedPractice}/${guidedPracticeUserId}`);
    } else {
      history.push(PATHS.guidedPractice);
      throw new Error(`handleGoToGuidedPractice -> Invalid status: ${guidedPractice.status}`);
    }
  };

  const filteredItems = useMemo(() => {
    return currentSubject === DEFAULT_SUBJECT
      ? data?.guidedPractices || []
      : (data?.guidedPractices || []).filter((item) => item.subject === currentSubject);
  }, [currentSubject, data]);

  const mappedItems = useMemo(() => {
    return filteredItems.map((item) => ({
      ...item,
      subject: subjects[item.subject],
    }));
  }, [filteredItems, subjects]);

  if (loading) return <Loading width={100} height={100} />;

  return (
    <Flex
      data-testid="guided-practice-list"
      height="100vh"
      overflow="hidden"
      flexDirection="column"
    >
      <Header backToMyCourse={handleBack} />
      <InnerLayout style={{ paddingLeft: isTablet ? 0 : 24, paddingRight: isTablet ? 0 : 24 }}>
        <TabList
          currentItemKey={currentSubject}
          setCurrentItemKey={setCurrentSubject}
          currentItem={
            filteredItems.length === 0 ? (
              <EmptyState title={t('EMPTY.TITLE')} description={t('EMPTY.DESCRIPTION')} />
            ) : (
              <GuidedPracticeListContent
                items={mappedItems}
                onClickItem={handleGoToGuidedPractice}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            )
          }
          scrollState={scrollState}
          setScrollState={setScrollState}
          // NOTE: For multiple subjects
          //  itemKeys={itemKeys}
          itemKeys={['all']}
          items={subjects}
        />
      </InnerLayout>
      <GuidedPracticeTutorialModal />
    </Flex>
  );
};

export default GuidedPracticeList;
